import apiClient from '../client'
import type {
  ModificationRequestResponse,
  DraftChangeResponse,
  StagedFileResponse,
} from '../models/response/modificationRequest'

const BASE_PATH = '/modification-requests'

export const modificationRequestService = {
  async list(expedienteCode?: string, status?: number): Promise<ModificationRequestResponse[]> {
    const params: string[] = []
    if (expedienteCode) params.push(`expedienteCode=${encodeURIComponent(expedienteCode)}`)
    if (status !== undefined && status !== null) params.push(`status=${status}`)
    const qs = params.length > 0 ? `?${params.join('&')}` : ''
    return apiClient.get<ModificationRequestResponse[]>(`${BASE_PATH}${qs}`)
  },

  async getById(id: string): Promise<ModificationRequestResponse> {
    // The backend currently exposes list only; filtering by id is done client-side via list.
    // For simplicity we fetch all and filter — fine for testing.
    const all = await apiClient.get<ModificationRequestResponse[]>(BASE_PATH)
    const hit = all.find(m => m.id === id)
    if (!hit) throw new Error(`MR ${id} no encontrada`)
    return hit
  },

  async create(expedienteCode: string, reason: string): Promise<string> {
    return apiClient.post<string>(BASE_PATH, { expedienteCode, reason })
  },

  async submit(id: string): Promise<void> {
    return apiClient.post<void>(`${BASE_PATH}/${id}/submit`)
  },

  async cancel(id: string): Promise<void> {
    return apiClient.post<void>(`${BASE_PATH}/${id}/cancel`)
  },

  async approve(id: string, reviewNotes?: string): Promise<void> {
    return apiClient.post<void>(`${BASE_PATH}/${id}/approve`, { reviewNotes })
  },

  async reject(id: string, reviewNotes?: string): Promise<void> {
    return apiClient.post<void>(`${BASE_PATH}/${id}/reject`, { reviewNotes })
  },

  // ── Draft change sandbox (M6.4) ──────────────────────────────────────────

  async listDraftChanges(id: string): Promise<DraftChangeResponse[]> {
    return apiClient.get<DraftChangeResponse[]>(`${BASE_PATH}/${id}/draft-changes`)
  },

  async upsertDraftChange(id: string, body: {
    entityName: string
    entityId: string | null
    operation: number
    proposedSnapshotJson: string
    originalSnapshotJson?: string | null
  }): Promise<string> {
    return apiClient.put<string>(`${BASE_PATH}/${id}/draft-changes`, body)
  },

  async removeDraftChange(id: string, changeId: string): Promise<void> {
    return apiClient.delete<void>(`${BASE_PATH}/${id}/draft-changes/${changeId}`)
  },

  async refreshExternalData(id: string): Promise<{
    ruc: string
    queriedAt: string
    sunatSucceeded: boolean
    recpoCount: number
    reinfoSource: string | null
    companyFieldsChanged: string[]
    miningSupplierFieldsChanged: string[]
    totalChanges: number
  }> {
    return apiClient.post(`${BASE_PATH}/${id}/refresh-external-data`)
  },

  async refreshMiningRightsData(id: string): Promise<{
    ruc: string
    queriedAt: string
    dmCount: number
    reinfoDmCount: number
    formalizedDmCount: number
    dmsWithChanges: Array<{ miningRightId: string; uniqueCode: string; changedFields: string[] }>
    totalChanges: number
  }> {
    return apiClient.post(`${BASE_PATH}/${id}/refresh-mining-rights`)
  },

  getStagedFileDownloadUrl(mrId: string, fileAttachmentId: string): string {
    return apiClient.buildUrl(`${BASE_PATH}/${mrId}/staged-files/${fileAttachmentId}/download`)
  },

  async uploadStagedFile(id: string, file: File): Promise<StagedFileResponse> {
    const formData = new FormData()
    formData.append('file', file)
    const url = apiClient.buildUrl(`${BASE_PATH}/${id}/staged-files`)
    const response = await apiClient.fetchAuthorized(url, { method: 'POST', body: formData })
    if (!response.ok) {
      const err = await response.text()
      throw new Error(err || 'Error al subir archivo staged')
    }
    return response.json()
  },
}
